import type { Path, UseFormReturn } from 'react-hook-form';
import { Disclosure, DisclosureButton, Labeled, Switch } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IGeneralSettingsFormValues } from './GeneralSettingsForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { useAppRegion } from '~/hooks/useAppRegion';

export type IGeneralSettingsFormAllArticlesSectionProps = IContainerProps & {
  form: UseFormReturn<IGeneralSettingsFormValues>;
  submittingFields?: Array<Path<IGeneralSettingsFormValues>>;
  onChange?: (
    form: UseFormReturn<IGeneralSettingsFormValues>,
    updatedFields?: Array<Path<IGeneralSettingsFormValues>>,
  ) => void;
};

export const GeneralSettingsFormAllArticlesSection: React.FC<
  IGeneralSettingsFormAllArticlesSectionProps
> = (props) => {
  const { sx, form, submittingFields, onChange } = props;
  const { t } = useTranslation();
  const appRegion = useAppRegion();

  const usersEnabled = form.watch('usersEnabled');
  const commentsEnabled =
    form.watch('communityArticleCommentsEnabled') ||
    form.watch('feedArticleCommentsEnabled');

  return (
    <Disclosure
      sx={sx}
      initiallyExpanded={
        form.watch('loginArticleRequired') ||
        form.watch('displayArticleAuthor') ||
        form.watch('displayArticleCommentsCount') ||
        form.watch('moderationRequired') ||
        form.watch('aiModerationEnabled') ||
        form.watch('displayArticleLikesViews') ||
        form.watch('displayPopularCategories') ||
        form.watch('displayTabsNames')
      }
      trigger={
        <DisclosureButton>
          {t('GeneralSettingsFormAllArticlesSection.title')}
        </DisclosureButton>
      }
    >
      <FormSection sub>
        <Labeled
          label={t(
            'GeneralSettingsFormAllArticlesSection.field.loginArticleRequired.label',
          )}
          hasError={!usersEnabled}
          errorText={
            !usersEnabled
              ? t('GeneralSettingsFormAllArticlesSection.error.usersRequired')
              : undefined
          }
          labelPosition="right"
        >
          <FormField
            name="loginArticleRequired"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['loginArticleRequired']),
            }}
            loading={submittingFields?.includes('loginArticleRequired')}
          />
        </Labeled>
        <Labeled
          label={t(
            'GeneralSettingsFormAllArticlesSection.field.displayArticleAuthor.label',
          )}
          labelPosition="right"
        >
          <FormField
            name="displayArticleAuthor"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['displayArticleAuthor']),
            }}
            loading={submittingFields?.includes('displayArticleAuthor')}
          />
        </Labeled>
        <Labeled
          label={t(
            'GeneralSettingsFormAllArticlesSection.field.displayArticleCommentsCount.label',
          )}
          hasError={!commentsEnabled}
          errorText={
            !commentsEnabled
              ? t(
                  'GeneralSettingsFormAllArticlesSection.error.commentsRequired',
                )
              : undefined
          }
          labelPosition="right"
        >
          <FormField
            name="displayArticleCommentsCount"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['displayArticleCommentsCount']),
            }}
            loading={submittingFields?.includes('displayArticleCommentsCount')}
          />
        </Labeled>
        <Labeled
          label={t(
            'GeneralSettingsFormAllArticlesSection.field.moderationRequired.label',
          )}
          hasError={!commentsEnabled}
          errorText={
            !commentsEnabled
              ? t(
                  'GeneralSettingsFormAllArticlesSection.error.commentsRequired',
                )
              : undefined
          }
          labelPosition="right"
        >
          <FormField
            name="moderationRequired"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['moderationRequired']),
            }}
            loading={submittingFields?.includes('moderationRequired')}
          />
        </Labeled>
        {appRegion.endsWith('-us') && (
          <Labeled
            label={t(
              'GeneralSettingsFormAllArticlesSection.field.aiModerationEnabled.label',
            )}
            supportingText={t(
              'GeneralSettingsFormAllArticlesSection.field.aiModerationEnabled.supportingText',
            )}
            labelPosition="right"
          >
            <FormField
              name="aiModerationEnabled"
              control={form.control}
              as={Switch}
              rules={{
                onChange: () => onChange?.(form, ['aiModerationEnabled']),
              }}
              loading={submittingFields?.includes('aiModerationEnabled')}
            />
          </Labeled>
        )}
        <Labeled
          label={t(
            'GeneralSettingsFormAllArticlesSection.field.displayArticleLikesViews.label',
          )}
          supportingText={t(
            'GeneralSettingsFormAllArticlesSection.field.displayArticleLikesViews.supportingText',
          )}
          hasError={!usersEnabled}
          errorText={
            !usersEnabled
              ? t('GeneralSettingsFormAllArticlesSection.error.usersRequired')
              : undefined
          }
          labelPosition="right"
        >
          <FormField
            name="displayArticleLikesViews"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['displayArticleLikesViews']),
            }}
            loading={submittingFields?.includes('displayArticleLikesViews')}
          />
        </Labeled>
        <Labeled
          label={t(
            'GeneralSettingsFormAllArticlesSection.field.displayPopularCategories.label',
          )}
          hasError={!usersEnabled}
          labelPosition="right"
        >
          <FormField
            name="displayPopularCategories"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['displayPopularCategories']),
            }}
            loading={submittingFields?.includes('displayPopularCategories')}
          />
        </Labeled>
      </FormSection>
    </Disclosure>
  );
};
